/**
 * Category Question Plan
 * ──────────────────────
 * The structured shape of a set of questionnaire questions proposed for a new
 * deal category - produced either by web research (the grounded "research online"
 * source) or by parsing custom pasted text, then written into the category's DRAFT
 * questionnaire template by `applyCategoryQuestions`.
 *
 * It mirrors the questionnaire template hierarchy (section → group → field) so the
 * apply step can revive-or-insert each level idempotently. `uiControl` is a control
 * KEY from the workspace's authorable-control catalog (list_field_controls); the
 * server derives `fieldType`/`valueShape` from it, so we keep it a free string here
 * (the catalog is per-workspace, not a fixed enum) and let the apply step validate.
 */
import { z } from 'zod';
export const CATEGORY_QUESTION_PLAN_VERSION = '1.0.0';
/** One option for an option-based control (select / multi-select / radio). */
export const categoryQuestionOptionSchema = z.object({
    label: z.string().min(1),
    value: z.string().min(1),
});
/** A single question (template field). */
export const categoryQuestionFieldSchema = z.object({
    /** Optional stable snake_case key; derived from the label when omitted. */
    fieldKey: z.string().optional(),
    label: z.string().min(1),
    /** Control key from list_field_controls (e.g. text, textarea, number, percentage, select). */
    uiControl: z.string().min(1),
    description: z.string().optional(),
    isRequired: z.boolean().optional(),
    /**
     * Whether AI should auto-fill this field from transcripts/documents. Defaults to true:
     * AI-authored questionnaires are filled by AI, so the question is AI-enabled unless an
     * author explicitly opts out.
     */
    aiEnabled: z.boolean().optional().default(true),
    /**
     * What the AI should look for in a transcript/document to fill this field. REQUIRED and
     * AI-WRITTEN (not a template): the model that authors/parses the plan must produce a
     * specific extraction prompt for every field.
     */
    aiPrompt: z.string().min(1),
    /** For option-based controls only. */
    options: z.array(categoryQuestionOptionSchema).optional(),
});
/** A group of related questions within a section. */
export const categoryQuestionGroupSchema = z.object({
    groupKey: z.string().min(1),
    label: z.string().min(1),
    description: z.string().optional(),
    fields: z.array(categoryQuestionFieldSchema).min(1),
});
/** A questionnaire section (top-level grouping shown as a wizard step). */
export const categoryQuestionSectionSchema = z.object({
    sectionKey: z.string().min(1),
    label: z.string().min(1),
    groups: z.array(categoryQuestionGroupSchema).min(1),
});
/** The full proposed question set for a category. */
export const categoryQuestionPlanSchema = z.object({
    sections: z.array(categoryQuestionSectionSchema).min(1),
});
//# sourceMappingURL=category-question-plan.js.map